import { axios } from 'setup/axiosSetup';

export type TeamRef = {
  name?: string;
  image_id?: string;
  cc?: string;
  id?: string;
  events_id?: string;
};

export type ScorePair = { home?: string; away?: string };

export type HistoryMatch = {
  id: string;
  time: string;
  ss?: string | null;
  /** First-half score ("1-0"), soccer only, when BetsAPI has it. */
  ht?: string;
  time_status?: string;
  league?: { name?: string; cc?: string | null };
  home: TeamRef;
  away: TeamRef;
};

export type TableRow = {
  pos: string;
  win: string;
  draw: string;
  loss: string;
  goalsfor: string;
  goalsagainst: string;
  points: string;
  promotion?: { name?: string; shortname?: string } | null;
  team: TeamRef;
};

export type LeagueTable = {
  season?: { name?: string };
  overall?: {
    tables?: { name?: string; groupname?: string | null; rows: TableRow[] }[];
  };
};

export type LineupPlayer = {
  player: { name: string; cc?: string };
  pos?: string;
  shirtnumber?: string;
};

export type TeamLineup = {
  formation?: string;
  startinglineup?: LineupPlayer[];
  substitutes?: LineupPlayer[];
};

export type MatchDetails = {
  fi: number;
  events_id?: string;
  sport_id: number;
  time: number;
  time_status: number;
  state: 'upcoming' | 'live' | 'ended';
  ss?: string;
  scores?: Record<string, ScorePair>;
  league: { name?: string; cc?: string; events_id?: string };
  home: TeamRef;
  away: TeamRef;
  stats?: Record<string, [string, string]>;
  timeline?: { id: string; text: string }[];
  extra?: {
    referee?: { name?: string; cc?: string };
    stadium_data?: {
      name?: string;
      city?: string;
      country?: string;
      capacity?: string;
    };
    home_manager?: { name?: string };
    away_manager?: { name?: string };
    round?: string;
    home_pos?: string;
    away_pos?: string;
  };
  has_lineup?: boolean;
  history?: {
    h2h?: HistoryMatch[];
    home?: HistoryMatch[];
    away?: HistoryMatch[];
  };
  table?: LeagueTable | LeagueTable[];
  lineup?: { home?: TeamLineup; away?: TeamLineup };
  updated_at?: number;
};

/** A match's statistics, timeline, form, table and lineups (served from our side). */
export const getMatchDetails = async (
  fi: number | string
): Promise<MatchDetails | null> => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';
  try {
    const { data } = await axios.get<MatchDetails>(
      `${baseUrl}/match_details?fi=${encodeURIComponent(String(fi))}`,
      { cache: false }
    );
    return data;
  } catch (e) {
    console.error(e);
    return null;
  }
};

/**
 * A team's crest, served by our nginx from its own cache of BetsAPI's
 * images (assets.b365api.com), so players never load them from there.
 */
/** A match's two crests. The live feed has none, so they come from the
 *  match's details, once a session per match. */
export type MatchCrests = { home?: string; away?: string };
const crests = new Map<string, Promise<MatchCrests>>();

export const getMatchCrests = (fi: number | string): Promise<MatchCrests> => {
  const key = String(fi);
  const known = crests.get(key);
  if (known) return known;
  const loading = getMatchDetails(fi).then((d) => {
    if (!d) crests.delete(key);
    return { home: d?.home?.image_id, away: d?.away?.image_id };
  });
  crests.set(key, loading);
  return loading;
};

export const teamLogoUrl = (imageId?: string, size: 's' | 'm' | 'b' = 's') =>
  imageId && imageId !== '0' ? `/img/team/${size}/${imageId}.png` : undefined;

/** A country's flag by its two-letter code (BetsAPI "cc"), via our nginx too;
 *  "gb-eng", "gb-sct", "gb-wls", "gb-nir" for the UK's (the API's FlagCC). */
export const flagUrl = (cc?: string | null) =>
  cc && /^([a-z]{2}|gb-(eng|sct|wls|nir))$/i.test(cc)
    ? `/img/flag/${cc.toLowerCase()}.png`
    : undefined;

/** The video of a match in play, when the admin has it on (see the API's
 *  internal/streams). `test`: the public test stream, not the match. */
export type MatchStream = { url: string; kind: 'hls'; test?: boolean };

/** `fresh`: the video is about to play; the provider's link carries a
 *  short-lived token, so the API asks for a new one instead of reusing it. */
export const getMatchStream = async (
  fi: number | string,
  fresh = false
): Promise<MatchStream | null> => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';
  try {
    const { data } = await axios.get<{
      available: boolean;
      stream?: MatchStream;
    }>(
      `${baseUrl}/match_stream?fi=${encodeURIComponent(String(fi))}${fresh ? '&fresh=1' : ''}`,
      {
        cache: false,
      }
    );
    return data?.available && data.stream?.url ? data.stream : null;
  } catch {
    // Not logged in, or video off.
    return null;
  }
};

/** The BetZone tracker of a football match in play: its gameid (SportAPI's
 *  "zp"), paired with our match on the server. */
export interface MatchTracker {
  gameid: number;
  sport: number;
}

export const getMatchTracker = async (
  fi: number | string
): Promise<MatchTracker | null> => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';
  try {
    const { data } = await axios.get<{
      available: boolean;
      tracker?: MatchTracker;
    }>(`${baseUrl}/match_tracker?fi=${encodeURIComponent(String(fi))}`, {
      cache: false,
    });
    return data?.available && data.tracker?.gameid ? data.tracker : null;
  } catch {
    return null;
  }
};
